import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DetailPanel, DetailProse } from './DetailPrimitives'
import './detail.css'

type DetailDescriptionPanelProps = {
  title: string
  subtitle?: string
  /** Already structured text (e.g. via formatStructuredDescription). */
  text: string
  emptyLabel?: string
  /** Collapse when content is longer than this many characters (default 320). */
  collapseAfterChars?: number
}

/**
 * Full-width description card with collapsed preview + expand / in-panel scroll.
 * Keeps long policy prose from pushing KPIs and actions off-screen.
 */
export function DetailDescriptionPanel({
  title,
  subtitle,
  text,
  emptyLabel,
  collapseAfterChars = 320,
}: DetailDescriptionPanelProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const trimmed = text?.trim() ?? ''
  const isEmpty = !trimmed
  const needsCollapse = useMemo(
    () => trimmed.length > collapseAfterChars || trimmed.split(/\n{2,}/).length > 2,
    [trimmed, collapseAfterChars]
  )

  return (
    <DetailPanel
      className="detail-panel--prose detail-panel--description"
      title={title}
      subtitle={subtitle}
    >
      {isEmpty ? (
        <DetailProse text="" emptyLabel={emptyLabel} />
      ) : (
        <div className="detail-desc">
          <div
            className={
              needsCollapse && !expanded
                ? 'detail-desc-body is-collapsed'
                : needsCollapse && expanded
                  ? 'detail-desc-body is-expanded'
                  : 'detail-desc-body'
            }
          >
            <DetailProse text={trimmed} emptyLabel={emptyLabel} />
          </div>
          {needsCollapse ? (
            <button
              type="button"
              className="detail-desc-toggle"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded
                ? t('detail.showLess', { defaultValue: 'Show less' })
                : t('detail.showMore', { defaultValue: 'Show more' })}
            </button>
          ) : null}
        </div>
      )}
    </DetailPanel>
  )
}
